import { ChangeEvent, memo, useMemo } from 'react';
import {
  PageSection,
  Stack,
  TextTitle,
  Icon,
  Spacer,
  DataTable,
} from '@pypestream/design-system';
import { ColumnDef } from '@tanstack/react-table';

import { DataFileType } from '../../utils';
import { Upload } from '../upload';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { useUploadPythonFiles } from '../../hooks';

export interface ActionFile {
  name: string;
  path: string;
  loading?: boolean;
  /** Stable key for an in-flight upload placeholder (PSTUDIO-3173). */
  id?: string;
}

export type RowSelectionChangeProps = ActionFile & {
  type: ActionSectionProps['type'];
};

export type ActionSectionProps = {
  title: string;
  label: string;
  columns: ColumnDef<ActionFile>[];
  data: ActionFile[];
  type: Exclude<DataFileType, 'assetData'>;
  accept?: string;
  viewOnly?: boolean;
  onRowSelection: (props: RowSelectionChangeProps) => void;
  /**
   * Project Bobcat — Build's Actions panel: a small heading and the table,
   * without the page section's card and spacing. Off, unchanged.
   */
  compact?: boolean;
  /** No upload button (the panel has its own "New action"). */
  hideUpload?: boolean;
  /**
   * Upload through the caller instead of Bot Manager (Build's Actions panel
   * writes to the Agent Manager). `uploadDisabled` then replaces the
   * draft-version check, which describes the Bot Manager upload.
   */
  onUpload?: (event: ChangeEvent<HTMLInputElement>) => void;
  uploadDisabled?: boolean;
};

export const ActionSection = memo(
  ({
    title,
    label,
    columns,
    data,
    type,
    accept,
    viewOnly,
    onRowSelection,
    compact,
    hideUpload,
    onUpload,
    uploadDisabled,
  }: ActionSectionProps) => {
    const { versionId } = useProStudioCtxSelector((ctx) => ({
      versionId:
        ctx.customerId && ctx.botName && ctx.draftVersion
          ? `${ctx.customerId}.${ctx.botName}.${ctx.draftVersion}`
          : undefined,
    }));

    const { uploadingFiles, handleUpload } = useUploadPythonFiles(type);

    const tableData = useMemo(() => {
      return [...data, ...uploadingFiles];
    }, [data, uploadingFiles]);

    const upload = !viewOnly && !hideUpload && (
      <Upload
        test-id={`ps-${type.toLowerCase()}-action-upload`}
        disabled={onUpload ? uploadDisabled : !versionId}
        label={label}
        accept={accept}
        onUpload={onUpload ?? handleUpload}
      />
    );
    const table = (
      <DataTable
        test-id={`ps-${type.toLowerCase()}-action-table`}
        size={compact ? 'xsmall' : 'small'}
        columns={columns}
        data={tableData}
        onRowSelectionChange={(event) => {
          if (event.detail.selected && event.detail.selected.length) {
            onRowSelection({
              name: event.detail.selected[0].original.name,
              path: event.detail.selected[0].original.path,
              type,
            });
          }
        }}
        enableRowSelection
      />
    );

    if (compact) {
      return (
        <div
          className="c-action-section--compact"
          data-testid={`ps-${type.toLowerCase()}-action-section`}
        >
          <div className="c-action-section__compact-head">
            <span className="c-action-section__compact-title">{title}</span>
            {upload}
          </div>
          {table}
        </div>
      );
    }

    return (
      <PageSection
        test-id={`ps-${type.toLowerCase()}-action-section`}
        size="small"
      >
        <Stack alignItems="center" justifyContent="space-between">
          <TextTitle
            test-id={`ps-${type.toLowerCase()}-action-title`}
            size="xsmall"
          >
            {title}
            <Icon
              test-id={`ps-${type.toLowerCase()}-action-title-icon`}
              name="info"
              color="gray"
              size="small"
              slot="suffix"
            />
          </TextTitle>
          {!viewOnly && (
            <Upload
              test-id={`ps-${type.toLowerCase()}-action-upload`}
              disabled={!versionId}
              label={label}
              accept={accept}
              onUpload={handleUpload}
            />
          )}
        </Stack>
        <Spacer size="large" />
        <DataTable
          test-id={`ps-${type.toLowerCase()}-action-table`}
          size="small"
          columns={columns}
          data={tableData}
          onRowSelectionChange={(event) => {
            if (event.detail.selected && event.detail.selected.length) {
              onRowSelection({
                name: event.detail.selected[0].original.name,
                path: event.detail.selected[0].original.path,
                type,
              });
            }
          }}
          enableRowSelection
        />
      </PageSection>
    );
  }
);
